/**
 * Nova Sticky PDP Navigation
 *
 * Responsibilities:
 *  - park the bar flush against the bottom of the sticky header (CSS `position: sticky`
 *    driven by the `--nova-sticky-pdp-nav-offset` custom property measured here);
 *  - keep the active pill in sync with the section currently under the bar (scrollspy),
 *    exactly as if the visitor had clicked it;
 *  - turn the pill row into a slider (native touch scrolling, pointer drag elsewhere);
 *  - reveal the mobile buy bar once the nav is stuck.
 */

const DRAG_THRESHOLD = 5;
const SCROLLER_EDGE_PADDING = 16;

class NovaStickyPdpNav extends HTMLElement {
	/** @type {HTMLElement | null} */
	#section = null;

	/** @type {HTMLElement | null} */
	#scroller = null;

	/** @type {{link: HTMLAnchorElement, target: HTMLElement}[]} */
	#entries = [];

	/** @type {number} Distance between the top of the viewport and the bottom of the header */
	#offset = 0;

	/** @type {number} */
	#activeIndex = -1;

	/** @type {boolean} */
	#pinned = false;

	/** @type {boolean} */
	#frameRequested = false;

	/** @type {boolean} */
	#dragging = false;

	/** @type {boolean} True between a drag that moved and the click it would otherwise fire */
	#dragMoved = false;

	#dragStartX = 0;
	#dragStartScroll = 0;

	/** @type {ResizeObserver | null} */
	#resizeObserver = null;

	connectedCallback() {
		this.#section = this.closest(".nova-sticky-pdp-nav-section");
		this.#scroller = this.querySelector(".nova-sticky-pdp-nav__scroller");

		this.#collectEntries();
		this.#measureOffset();
		this.#updateDraggable();

		window.addEventListener("scroll", this.#requestUpdate, {passive: true});
		window.addEventListener("resize", this.#handleResize, {passive: true});
		// Fonts and lazy media shift every target — re-measure once the page settles.
		window.addEventListener("load", this.#handleLoad);
		this.addEventListener("click", this.#handleClick);

		if (this.#scroller) {
			this.#scroller.addEventListener("pointerdown", this.#handlePointerDown);
			window.addEventListener("pointermove", this.#handlePointerMove);
			window.addEventListener("pointerup", this.#handlePointerUp);
			window.addEventListener("pointercancel", this.#handlePointerUp);
		}

		// The header can change height (search opening, announcement bar, resize).
		const headerSection = this.#headerSection();
		if (headerSection && "ResizeObserver" in window) {
			this.#resizeObserver = new ResizeObserver(this.#handleResize);
			this.#resizeObserver.observe(headerSection);
		}

		this.#update();
	}

	disconnectedCallback() {
		window.removeEventListener("scroll", this.#requestUpdate);
		window.removeEventListener("resize", this.#handleResize);
		window.removeEventListener("load", this.#handleLoad);
		this.removeEventListener("click", this.#handleClick);
		window.removeEventListener("pointermove", this.#handlePointerMove);
		window.removeEventListener("pointerup", this.#handlePointerUp);
		window.removeEventListener("pointercancel", this.#handlePointerUp);
		this.#scroller?.removeEventListener("pointerdown", this.#handlePointerDown);
		this.#resizeObserver?.disconnect();
	}

	/* ---------------------------------------------------------------- *
	 * Measuring
	 * ---------------------------------------------------------------- */

	/** @returns {HTMLElement | null} The element that actually sticks, when the header is sticky */
	#headerSection() {
		const header = document.querySelector("#header-component");
		if (!header) return null;
		return header.closest(".header-section") ?? header;
	}

	/**
	 * Writes the distance from the top of the viewport to the bottom of the sticky header
	 * onto the section, so the bar comes to rest right underneath it — never over or under.
	 */
	#measureOffset() {
		const headerSection = this.#headerSection();
		let offset = 0;

		if (headerSection) {
			const styles = getComputedStyle(headerSection);
			if (styles.position === "sticky" || styles.position === "fixed") {
				const top = Number.parseFloat(styles.top);
				offset = Math.max(0, headerSection.offsetHeight + (Number.isNaN(top) ? 0 : top));
			}
		}

		this.#offset = offset;
		this.#section?.style.setProperty("--nova-sticky-pdp-nav-offset", `${offset}px`);
	}

	/** @returns {number} Document Y coordinate the bar rests at once stuck */
	#restLine() {
		return this.#offset + this.offsetHeight;
	}

	#collectEntries() {
		const links = /** @type {HTMLAnchorElement[]} */ ([
			...this.querySelectorAll(".nova-sticky-pdp-nav__link"),
		]);

		this.#entries = links.reduce((entries, link) => {
			const id = link.dataset.navTarget;
			const target = id ? document.getElementById(id) : null;
			if (target) entries.push({link, target});
			return entries;
		}, /** @type {{link: HTMLAnchorElement, target: HTMLElement}[]} */ ([]));

		// Force the next pass to reapply the highlight against the new list.
		this.#activeIndex = -1;
	}

	/* ---------------------------------------------------------------- *
	 * Scroll handling
	 * ---------------------------------------------------------------- */

	#requestUpdate = () => {
		if (this.#frameRequested) return;
		this.#frameRequested = true;
		requestAnimationFrame(() => {
			this.#frameRequested = false;
			this.#update();
		});
	};

	#update() {
		this.#updateActiveLink();
		this.#updatePinnedState();
	}

	/**
	 * Highlights the last link whose target has passed under the bar, which mirrors
	 * the state a click on that link would leave behind.
	 */
	#updateActiveLink() {
		if (this.#entries.length === 0) return;

		const restLine = this.#restLine();
		let index = 0;

		this.#entries.forEach((entry, i) => {
			// A 2px tolerance absorbs the sub-pixel rounding of smooth scrolling.
			if (entry.target.getBoundingClientRect().top - restLine <= 2) index = i;
		});

		// The last target can be too short to ever reach the bar — claim it at page end.
		const atPageEnd =
			window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
		if (atPageEnd) index = this.#entries.length - 1;

		if (index === this.#activeIndex) return;
		this.#activeIndex = index;

		this.#entries.forEach((entry, i) => {
			const isActive = i === index;
			entry.link.classList.toggle("nova-sticky-pdp-nav__link--active", isActive);
			if (isActive) {
				entry.link.setAttribute("aria-current", "true");
			} else {
				entry.link.removeAttribute("aria-current");
			}
		});

		this.#revealLink(this.#entries[index].link);
	}

	/** Shows the mobile buy bar from the moment the nav parks under the header. */
	#updatePinnedState() {
		if (!this.#section) return;

		const pinned = this.#section.getBoundingClientRect().top <= this.#offset + 1;
		if (pinned === this.#pinned) return;

		this.#pinned = pinned;
		this.classList.toggle("nova-sticky-pdp-nav--pinned", pinned);
	}

	#handleResize = () => {
		this.#measureOffset();
		this.#updateDraggable();
		this.#update();
	};

	#handleLoad = () => {
		this.#collectEntries();
		this.#handleResize();
	};

	/* ---------------------------------------------------------------- *
	 * Slider
	 * ---------------------------------------------------------------- */

	#canScroll() {
		if (!this.#scroller) return false;
		return this.#scroller.scrollWidth - this.#scroller.clientWidth > 1;
	}

	#updateDraggable() {
		this.#scroller?.classList.toggle("nova-sticky-pdp-nav__scroller--draggable", this.#canScroll());
	}

	/** Keeps the active pill inside the visible part of the slider. */
	#revealLink(link) {
		const scroller = this.#scroller;
		if (!scroller || !this.#canScroll()) return;

		const linkRect = link.getBoundingClientRect();
		const scrollerRect = scroller.getBoundingClientRect();
		const left = linkRect.left - scrollerRect.left + scroller.scrollLeft;
		const right = left + linkRect.width;

		if (left < scroller.scrollLeft + SCROLLER_EDGE_PADDING) {
			scroller.scrollLeft = Math.max(0, left - SCROLLER_EDGE_PADDING);
		} else if (right > scroller.scrollLeft + scroller.clientWidth - SCROLLER_EDGE_PADDING) {
			scroller.scrollLeft = right - scroller.clientWidth + SCROLLER_EDGE_PADDING;
		}
	}

	#handlePointerDown = event => {
		// Touch already gets native momentum scrolling — don't fight it.
		if (event.pointerType === "touch" || !this.#canScroll()) return;

		this.#dragging = true;
		this.#dragMoved = false;
		this.#dragStartX = event.clientX;
		this.#dragStartScroll = this.#scroller.scrollLeft;
	};

	#handlePointerMove = event => {
		if (!this.#dragging) return;

		const delta = event.clientX - this.#dragStartX;
		if (!this.#dragMoved && Math.abs(delta) < DRAG_THRESHOLD) return;

		if (!this.#dragMoved) {
			this.#dragMoved = true;
			this.#scroller.classList.add("nova-sticky-pdp-nav__scroller--dragging");
		}

		event.preventDefault();
		this.#scroller.scrollLeft = this.#dragStartScroll - delta;
	};

	#handlePointerUp = () => {
		if (!this.#dragging) return;

		this.#dragging = false;
		this.#scroller.classList.remove("nova-sticky-pdp-nav__scroller--dragging");

		// The click event fires right after pointerup; clear the flag once it has passed.
		if (this.#dragMoved) setTimeout(() => (this.#dragMoved = false), 0);
	};

	/* ---------------------------------------------------------------- *
	 * Navigation
	 * ---------------------------------------------------------------- */

	#handleClick = event => {
		const link = event.target.closest?.(".nova-sticky-pdp-nav__link");
		if (!link) return;

		// A drag that ended on a pill must not navigate.
		if (this.#dragMoved) {
			event.preventDefault();
			return;
		}

		const entry = this.#entries.find(candidate => candidate.link === link);
		if (!entry) return;

		event.preventDefault();

		const top = entry.target.getBoundingClientRect().top + window.scrollY - this.#restLine();
		const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		window.scrollTo({top: Math.max(0, top), behavior: reducedMotion ? "auto" : "smooth"});
	};
}

if (!customElements.get("nova-sticky-pdp-nav")) {
	customElements.define("nova-sticky-pdp-nav", NovaStickyPdpNav);
}
